import { HeaderIconLinkRow, Link } from '@backstage/core-components';
import { alertApiRef, configApiRef, errorApiRef, useApi } from '@backstage/core-plugin-api';
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  getCompoundEntityRef,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { AboutContent } from '@backstage/plugin-catalog';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogApiRef, EntityInfoCard, getEntitySourceLocation, useEntity } from '@backstage/plugin-catalog-react';
import { scmIntegrationsApiRef, ScmIntegrationIcon } from '@backstage/integration-react';
import {
  TECHDOCS_ANNOTATION,
  TECHDOCS_EXTERNAL_ANNOTATION,
  TECHDOCS_EXTERNAL_PATH_ANNOTATION,
} from '@backstage/plugin-techdocs-common';
import IconButton from '@material-ui/core/IconButton';
import CachedIcon from '@material-ui/icons/Cached';
import DocsIcon from '@material-ui/icons/Description';
import EditIcon from '@material-ui/icons/Edit';
import ViewListIcon from '@material-ui/icons/ViewList';
import { makeStyles } from '@material-ui/core/styles';
import { useMemo } from 'react';
import { useAsync } from 'react-use';
import {
  buildGroupIndex,
  parseOwnerGroupName,
  readJiraProjectKeys,
  resolveEffectiveProjectKeys,
} from '../organization/organizationBoardResolution';

const useStyles = makeStyles({
  linkContainer: {
    border: '1px solid var(--bui-border-1)',
    borderLeft: 'none',
    borderRight: 'none',
    marginBottom: 'var(--bui-space-6)',
  },
});

// Mirrors @backstage/plugin-techdocs-react's buildTechDocsURL: TechDocs may be
// hosted on a *different* entity (`backstage.io/techdocs-entity`) with an
// optional sub-path (`backstage.io/techdocs-entity-path`), so the link must
// target that referenced entity rather than always this one.
function buildTechDocsHref(entity: Parameters<typeof getCompoundEntityRef>[0]): string {
  let { namespace, kind, name } = getCompoundEntityRef(entity);
  const externalRef = entity.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION];
  if (externalRef) {
    try {
      ({ namespace, kind, name } = parseEntityRef(externalRef));
    } catch {
      // Fall back to this entity's own docs if the ref is malformed.
    }
  }
  let subPath = entity.metadata.annotations?.[TECHDOCS_EXTERNAL_PATH_ANNOTATION] ?? '';
  if (subPath && !subPath.startsWith('/')) {
    subPath = `/${subPath}`;
  }
  return `/docs/${namespace}/${kind.toLocaleLowerCase('en-US')}/${name}${subPath}`;
}

export const EntityAboutWithJiraBoardCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const configApi = useApi(configApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const { allowed: canRefresh } = useEntityPermission(catalogEntityRefreshPermission);

  const sourceLocation = getEntitySourceLocation(entity, scmIntegrationsApi);
  const hasTechdocs =
    Boolean(entity.metadata.annotations?.[TECHDOCS_ANNOTATION]) ||
    Boolean(entity.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION]);

  // The entity's own keys are known synchronously, so an annotated entity shows
  // JIRA BOARD enabled on first paint (no flicker). `readJiraProjectKeys` also
  // handles lowercase/quoted keys, legacy `atlassian.net/jql`,
  // `spec.jiraProject`, and drops the `YOU_JIRA_KEY` placeholder.
  const explicitJiraKeys = useMemo(() => readJiraProjectKeys(entity), [entity]);

  // Only when the entity has no keys of its own do we fetch groups to resolve
  // keys inherited from its owner-group chain (same logic the organization
  // views use), so inheriting components still get an enabled link.
  const inheritedJiraKeys = useAsync(async () => {
    if (explicitJiraKeys.length > 0 || !parseOwnerGroupName(entity.spec?.owner)) {
      return [] as string[];
    }
    const { items } = await catalogApi.getEntities({ filter: { kind: 'Group' } });
    const groupIndex = buildGroupIndex(items);
    const groupEntitiesByName = new Map(items.map(group => [group.metadata.name, group]));
    return resolveEffectiveProjectKeys(entity, groupIndex, groupEntitiesByName).projectKeys;
  }, [catalogApi, entity, explicitJiraKeys]);

  const jiraKeys = explicitJiraKeys.length > 0 ? explicitJiraKeys : inheritedJiraKeys.value ?? [];
  const primaryJiraKey = jiraKeys[0];
  const jiraBaseUrl =
    configApi.getOptionalString('jira.baseUrl') ?? 'https://invisible.atlassian.net';
  // Use the `/browse/{KEY}` project shortcut: Jira redirects it to the project's
  // board for both team-managed and company-managed projects. The explicit
  // `/jira/software/projects/{key}/boards` path only resolves for team-managed
  // projects and 404s/misroutes for company-managed ones.
  const jiraBoardUrl = primaryJiraKey
    ? `${jiraBaseUrl}/browse/${encodeURIComponent(primaryJiraKey)}`
    : undefined;
  const entityMetadataEditUrl = entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
  const entityLocation = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');

  const links = [
    {
      label: 'View Source',
      disabled: !sourceLocation,
      icon: <ScmIntegrationIcon type={sourceLocation?.integrationType} />,
      href: sourceLocation?.locationTargetUrl,
    },
    {
      label: 'View TechDocs',
      disabled: !hasTechdocs,
      icon: <DocsIcon />,
      href: hasTechdocs ? buildTechDocsHref(entity) : undefined,
    },
    {
      label: 'JIRA BOARD',
      disabled: !jiraBoardUrl,
      icon: <ViewListIcon />,
      href: jiraBoardUrl,
    },
  ];

  const refreshEntity = async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Refresh scheduled',
        severity: 'info',
        display: 'transient',
      });
    } catch (error) {
      errorApi.post(error instanceof Error ? error : new Error(String(error)));
    }
  };

  return (
    <EntityInfoCard
      title="About"
      headerActions={
        <>
          {allowRefresh && canRefresh && (
            <IconButton
              aria-label="Refresh entity"
              title="Refresh"
              onClick={refreshEntity}
            >
              <CachedIcon />
            </IconButton>
          )}
          <IconButton
            component={Link}
            aria-label="Edit metadata"
            disabled={!entityMetadataEditUrl}
            title="Edit"
            to={entityMetadataEditUrl ?? '#'}
          >
            <EditIcon />
          </IconButton>
        </>
      }
    >
      <div className={classes.linkContainer}>
        <HeaderIconLinkRow links={links} />
      </div>
      <AboutContent entity={entity} />
    </EntityInfoCard>
  );
};
